import React, { FC, useState } from 'react';
import { Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from '../components/loadingSpinner/loadingSpinner';
import PauseButton from '../assets/icons/pause-icon.svg';
import PlayButton from '../assets/icons/play-icon.svg';
import DisabledPauseButton from '../assets/icons/disabled-pause-icon.svg';
import closableEnqueueSnackbar from '../components/closableEnqueueSnackbar/closableEnqueueSnackbar';
import { useStartAdMutation, useStopAdMutation } from '../apiHooks/mutations/adManagementMutations/startOrStopAdMutation';
import { TrackingSourceEnum } from '../components/enums/trackingSourceEnum';

interface PlayStopAdCellProps {
  params?: boolean;
  rowObject?: any;
  sizePx?: number;
  className?: string;
}

const PlayStopAdCell: FC<PlayStopAdCellProps> = (props) => {
  const { t } = useTranslation();
  const [isStopped, setIsStopped] = useState(!props.params);
  const [isProcessing, setIsProcessing] = useState(false);
  const sizePx = props.sizePx ?? 30;

  const onStartSuccess = () => {
    closableEnqueueSnackbar(t('adStartSuccess'), 'success');
  };

  const onStopSuccess = () => {
    closableEnqueueSnackbar(t('adStopSuccess'), 'success');
  };

  const {
    mutateAsync: startAd,
  } = useStartAdMutation(props.rowObject.trackingSourceId, onStartSuccess);

  const {
    mutateAsync: stopAd,
  } = useStopAdMutation(props.rowObject.trackingSourceId, onStopSuccess);

  const handleStartAd = async () => {
    if (!props.rowObject?.adId || isProcessing) return;
    setIsProcessing(true);

    setIsStopped(false);

    try {
      await startAd(String(props.rowObject.adId));
    } catch (error) {
      setIsStopped(true);
      closableEnqueueSnackbar(t('adStartError'), 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleStopAd = async () => {
    if (!props.rowObject?.adId || isProcessing) return;
    setIsProcessing(true);

    setIsStopped(true);

    try {
      await stopAd(String(props.rowObject.adId));
    } catch (error) {
      setIsStopped(false);
      closableEnqueueSnackbar(t('adStopError'), 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  if (
    props.rowObject.trackingSourceId !== TrackingSourceEnum.Google
      && props.rowObject.trackingSourceId !== TrackingSourceEnum.Facebook
  ) {
    return (
      <button
        type="button"
        disabled
        style={{ height: sizePx, width: sizePx }}
        className={`grid cursor-not-allowed place-content-center rounded-md border border-dark bg-white ${props.className ?? ''}`}
      >
        <Tooltip arrow placement="right" title={t('cannotBeControlled')} className="h-full w-full p-1.5">
          <img src={DisabledPauseButton} alt="disabled pause button icon" className="h-3 w-3 object-contain" />
        </Tooltip>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={isStopped ? handleStartAd : handleStopAd}
      disabled={isProcessing}
      style={{ height: sizePx, width: sizePx }}
      className={`grid place-content-center rounded-md border border-dark bg-white duration-150
        hover:border-primary/50 active:border-primary ${isProcessing ? 'cursor-not-allowed opacity-50' : ''} ${props.className ?? ''}`}
    >
      {isProcessing ? (
        <LoadingSpinner size={12} color="primary" center />
      ) : (
        <Tooltip
          arrow
          placement="top"
          title={isStopped ? t('startAd') : t('stopAd')}
          className="h-full w-full p-2"
        >
          <img
            src={isStopped ? PlayButton : PauseButton}
            alt={isStopped ? 'play button icon' : 'pause button icon'}
            className="h-3 w-3 object-contain"
          />
        </Tooltip>
      )}
    </button>
  );
};

export default PlayStopAdCell;
